import { isValidElement, type ButtonHTMLAttributes, type ReactNode } from "react";
import { IconeBaixar, IconeDuplicar, IconeImprimir, IconeLixeira, IconeOlho } from "../icons";
import { useIdioma } from "../../i18n/IdiomaProvider";
import styles from "./BotaoAcao.module.css";

export type VarianteBotao = "primario" | "secundario" | "perigo" | "ghost";
export type EstiloBotao = "padrao" | "chip" | "topo";

export interface BotaoAcaoProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: VarianteBotao;
  estilo?: EstiloBotao;
  iconeInicio?: ReactNode;
  carregando?: boolean;
}

export interface BotaoContornoProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  iconeInicio?: ReactNode;
  carregando?: boolean;
  somenteIcone?: boolean;
  estilo?: "padrao" | "topo";
}

function textoDoBotao(children: ReactNode): string {
  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }

  if (Array.isArray(children)) {
    return children.map(textoDoBotao).join(" ");
  }

  if (isValidElement<{ children?: ReactNode }>(children)) {
    return textoDoBotao(children.props.children);
  }

  return "";
}

function normalizarTextoBotao(children: ReactNode): string {
  return textoDoBotao(children)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function iconePadraoPorRotulo(children: ReactNode): ReactNode {
  const rotulo = normalizarTextoBotao(children);

  if (rotulo === "visualizar") return <IconeOlho />;
  if (rotulo.startsWith("duplicar")) return <IconeDuplicar />;
  if (rotulo.startsWith("excluir")) return <IconeLixeira />;
  if (rotulo.startsWith("exportar")) return <IconeBaixar />;
  if (rotulo === "imprimir") return <IconeImprimir />;
  return null;
}

function traduzirFilhoBotao(children: ReactNode, traduzir: (texto: string) => string): ReactNode {
  if (typeof children === "string") return traduzir(children);
  return children;
}

/**
 * Botão de ação personalizado (NUNCA usar o botão padrão do navegador).
 * Segue o padrão visual do sistema: primário em teal, secundário com borda,
 * perigo em vermelho, ghost transparente. Respeita os temas claro/escuro.
 */
export function BotaoAcao({
  variante = "primario",
  estilo = "padrao",
  iconeInicio,
  carregando = false,
  children,
  className,
  disabled,
  type,
  ...rest
}: BotaoAcaoProps) {
  const { traduzir } = useIdioma();
  const classes = [styles.botao, styles[estilo], "botao-acao", `botao-acao--${variante}`, styles[variante], className]
    .filter(Boolean)
    .join(" ");
  const iconeInicioResolvido = iconeInicio ?? iconePadraoPorRotulo(children);
  const childrenTraduzido = traduzirFilhoBotao(children, traduzir);

  return (
    <button
      type={type ?? "button"}
      className={classes}
      disabled={disabled || carregando}
      aria-busy={carregando || undefined}
      {...rest}
    >
      {carregando ? (
        <span className={styles.spinner} aria-hidden="true" />
      ) : (
        iconeInicioResolvido && <span className={`${styles.icone} botao-acao__icone`}>{iconeInicioResolvido}</span>
      )}
      {children != null && <span className={`${styles.rotulo} botao-acao__rotulo`}>{childrenTraduzido}</span>}
    </button>
  );
}

export function BotaoContorno({
  iconeInicio,
  carregando = false,
  somenteIcone = false,
  estilo = "padrao",
  children,
  className,
  disabled,
  type,
  ...rest
}: BotaoContornoProps) {
  const { traduzir } = useIdioma();
  const classes = [
    styles.contorno,
    estilo === "topo" ? styles.contornoTopo : "",
    somenteIcone ? styles.contornoSomenteIcone : "",
    "botao-contorno",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const iconeInicioResolvido = iconeInicio ?? iconePadraoPorRotulo(children);
  const childrenTraduzido = traduzirFilhoBotao(children, traduzir);

  return (
    <button
      type={type ?? "button"}
      className={classes}
      disabled={disabled || carregando}
      aria-busy={carregando || undefined}
      {...rest}
    >
      {carregando ? (
        <span className={styles.spinner} aria-hidden="true" />
      ) : (
        iconeInicioResolvido && <span className={styles.icone}>{iconeInicioResolvido}</span>
      )}
      {!somenteIcone && children != null && <span className={styles.rotulo}>{childrenTraduzido}</span>}
    </button>
  );
}
